"use client";

import { type CSSProperties, type ComponentPropsWithoutRef, type ElementType, type ReactNode } from "react";
import { GradientBorderCard } from "@/components/ui/GradientBorderCard";

type SurfaceElementProps<T extends ElementType> = {
  as?: T;
  children: ReactNode;
  className?: string;
  surface: "slab" | "card";
} & Omit<ComponentPropsWithoutRef<T>, "as" | "children" | "className" | "style">;

function getSurfaceStyle(surface: "slab" | "card"): CSSProperties {
  if (surface === "slab") {
    return {
      background: "var(--home-slab-bg)",
      border: "1px solid var(--home-slab-border)",
    };
  }

  return {
    background: "var(--home-card-bg)",
    border: "1px solid var(--home-card-border)",
  };
}

export function HomepageSurface<T extends ElementType = "div">({
  as,
  children,
  className,
  surface,
  ...rest
}: SurfaceElementProps<T>) {
  const Tag = (as ?? "div") as ElementType;

  return (
    <Tag
      className={className}
      style={getSurfaceStyle(surface)}
      {...rest}
    >
      {children}
    </Tag>
  );
}

type HomepageGradientBorderCardProps = ComponentPropsWithoutRef<typeof GradientBorderCard>;

export function HomepageGradientBorderCard({
  children,
  className,
  innerClassName,
  style,
  ...rest
}: HomepageGradientBorderCardProps) {
  const themedStyle = {
    ...style,
    ["--home-card-bg" as string]: "var(--home-card-bg)",
    ["--home-card-border" as string]: "var(--home-card-border)",
  } as CSSProperties;

  return (
    <GradientBorderCard
      className={className}
      innerClassName={`border-[var(--home-card-border)] bg-[var(--home-card-bg)]${innerClassName ? ` ${innerClassName}` : ""}`}
      style={themedStyle}
      {...rest}
    >
      {children}
    </GradientBorderCard>
  );
}
